import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, Container, Table, Spinner, Alert, Row, Col, Card, Form, Collapse } from 'react-bootstrap';
import { getIncomes, deleteIncome } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Pagination from '../components/Pagination';
import PageSizeSelector from '../components/PageSizeSelector';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';
import { FaEdit, FaTrash, FaTable, FaTh } from 'react-icons/fa';
import CreateIncomeModal from '../components/CreateIncomeModal';

const INCOME_CATEGORIES = ['Loan', 'CapitalInjection', 'Refund', 'Interest', 'AssetSale', 'Grant', 'Other'];

const getLocalISODate = () => {
    const date = new Date();
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const IncomesPage = () => {
    const { user, business } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t } = useTranslation();

    const isSubscriptionActive = ['active', 'trialing'].includes(business?.subscription_status);
    const featureEnabled = !!business?.plan_features?.incomes;

    const [incomes, setIncomes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingIncome, setEditingIncome] = useState(null);

    const today = getLocalISODate();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalAmount, setTotalAmount] = useState(0);

    const [filters, setFilters] = useState({ category: '', startDate: today, endDate: today });
    const [activeFilters, setActiveFilters] = useState({ category: '', startDate: today, endDate: today });
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('incomesViewMode') || 'table');

    const toggleViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('incomesViewMode', mode);
    };

    const formatCurrency = (value) => `$${Number(value || 0).toLocaleString()}`;

    const fetchIncomes = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: pageSize,
                category: activeFilters.category || undefined,
                startDate: activeFilters.startDate || undefined,
                endDate: activeFilters.endDate || undefined,
            };
            const response = await getIncomes(params);
            setIncomes(response.data.incomes);
            setTotalPages(response.data.totalPages);
            setTotalAmount(response.data.totalAmount);
            setError('');
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 403) {
                setError(err.response.data.message || t('incomesPage.toasts.permissionError'));
            } else {
                toast.error(t('incomesPage.toasts.fetchFailed'));
            }
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, activeFilters, t]);

    useEffect(() => {
        if (hasPermission(PERMISSIONS.VIEW_INCOMES)) {
            fetchIncomes();
        } else {
            setLoading(false);
            setError(t('incomesPage.toasts.permissionError'));
        }
    }, [fetchIncomes, hasPermission, t]);

    const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
    const applyFilters = () => { setCurrentPage(1); setActiveFilters(filters); };
    const clearFilters = () => {
        const initial = { category: '', startDate: today, endDate: today };
        setFilters(initial);
        setActiveFilters(initial);
        setCurrentPage(1);
    };

    const handleModalClose = () => { setShowModal(false); setEditingIncome(null); };
    const handleCreateClick = () => { setEditingIncome(null); setShowModal(true); };
    const handleEditClick = (income) => { setEditingIncome(income); setShowModal(true); };

    const handleDelete = async (incomeId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('incomesPage.toasts.deleteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('incomesPage.actions.delete'),
            cancelButtonText: t('cancel'),
        });
        if (!result.isConfirmed) return;
        const toastId = toast.loading(t('incomesPage.toasts.deleting'));
        try {
            await deleteIncome(incomeId);
            fetchIncomes();
            trackAction('income_deleted');
            toast.success(t('incomesPage.toasts.deleted'), { id: toastId });
        } catch (err) {
            toast.error(t('incomesPage.toasts.deleteFailed'), { id: toastId });
        }
    };

    return (
        <Container className="mt-4">
            <Row className="align-items-center mb-4">
                <Col md={6}>
                    <h2>{t('incomesPage.title')}</h2>
                    <p className="text-muted mb-0">{t('incomesPage.subtitle')}</p>
                </Col>
                <Col md={3}>
                    <Card className="shadow-sm border-0 text-center">
                        <Card.Body className="py-3">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: '0.8rem' }}>
                                {t('incomesPage.totalLabel')}
                            </h6>
                            <div className="fw-bold text-success" style={{ fontSize: 'clamp(0.9rem, 3vw, 1.3rem)' }}>
                                {formatCurrency(totalAmount)}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} className="text-end">
                    {hasPermission(PERMISSIONS.CREATE_INCOMES) && (
                        <div
                            title={!isSubscriptionActive ? t('subscriptionRequired') : !featureEnabled ? t('common.featureNotAvailable') : ''}
                            className="d-inline-block"
                        >
                            <Button
                                variant="success"
                                onClick={handleCreateClick}
                                disabled={!isSubscriptionActive || !featureEnabled}
                            >
                                {t('incomesPage.newIncomeButton')}
                            </Button>
                        </div>
                    )}
                </Col>
            </Row>

            <div className="d-md-none mb-3">
                <Button variant="link" onClick={() => setShowFilters(!showFilters)} className="p-0 text-decoration-none">
                    {showFilters ? t('incomesPage.filter.hideFilters') : t('incomesPage.filter.viewFilters')}
                </Button>
            </div>

            <Collapse in={showFilters || window.innerWidth >= 768}>
                <div id="incomes-filters">
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Body>
                            <Form>
                                <Row className="align-items-end">
                                    <Col md={3}>
                                        <Form.Group className="mb-2 mb-md-0">
                                            <Form.Label>{t('incomesPage.filter.allCategories')}</Form.Label>
                                            <Form.Select name="category" value={filters.category} onChange={handleFilterChange}>
                                                <option value="">{t('incomesPage.filter.allCategories')}</option>
                                                {INCOME_CATEGORIES.map((c) => (
                                                    <option key={c} value={c}>{t(`incomesPage.categories.${c}`)}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2 mb-md-0">
                                            <Form.Label>{t('incomesPage.filter.startDate')}</Form.Label>
                                            <Form.Control type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2 mb-md-0">
                                            <Form.Label>{t('incomesPage.filter.endDate')}</Form.Label>
                                            <Form.Control type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3} className="d-flex mt-2 mt-md-0">
                                        <Button variant="primary" onClick={applyFilters} className="me-2 flex-grow-1 flex-md-grow-0">
                                            {t('incomesPage.filter.apply')}
                                        </Button>
                                        <Button variant="secondary" onClick={clearFilters} className="flex-grow-1 flex-md-grow-0">
                                            {t('incomesPage.filter.clear')}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </Collapse>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* View Mode Toggle */}
            <div className="d-flex justify-content-end align-items-center mb-3">
                <div
                    className="sales-view-toggle"
                    role="group"
                    aria-label={t('incomesPage.viewToggle.label', 'Cambiar vista')}
                >
                    <button
                        id="incomes-view-table"
                        className={`sales-view-toggle__btn${viewMode === 'table' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('table')}
                        aria-pressed={viewMode === 'table'}
                        title={t('incomesPage.viewToggle.tableView', 'Vista tabla')}
                        aria-label={t('incomesPage.viewToggle.tableView', 'Vista tabla')}
                    >
                        <FaTable aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('incomesPage.viewToggle.tableView', 'Tabla')}</span>
                    </button>
                    <button
                        id="incomes-view-cards"
                        className={`sales-view-toggle__btn${viewMode === 'cards' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('cards')}
                        aria-pressed={viewMode === 'cards'}
                        title={t('incomesPage.viewToggle.cardsView', 'Vista tarjetas')}
                        aria-label={t('incomesPage.viewToggle.cardsView', 'Vista tarjetas')}
                    >
                        <FaTh aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('incomesPage.viewToggle.cardsView', 'Tarjetas')}</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
                <>
                    {viewMode === 'table' ? (
                    <div className="modern-table-wrapper mt-1">
                        <Table hover responsive className="modern-table">
                            <thead>
                                <tr>
                                    <th>{t('incomesPage.table.date')}</th>
                                    <th>{t('incomesPage.table.category')}</th>
                                    <th>{t('incomesPage.table.description')}</th>
                                    <th>{t('incomesPage.table.paymentMethod')}</th>
                                    <th>{t('incomesPage.table.registeredBy')}</th>
                                    <th className="text-end">{t('incomesPage.table.amount')}</th>
                                    <th className="text-center">{t('incomesPage.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {incomes.length > 0 ? incomes.map((income) => (
                                    <tr key={income.income_id}>
                                        <td>{new Date(income.date).toLocaleDateString('es-CO', { timeZone: 'UTC' })}</td>
                                        <td>
                                            <span className="badge bg-light text-dark border p-2">
                                                {t(`incomesPage.categories.${income.category}`, income.category)}
                                            </span>
                                        </td>
                                        <td>{income.description}</td>
                                        <td>{t(`incomesPage.paymentMethods.${income.payment_method}`, income.payment_method || '-')}</td>
                                        <td className="text-muted small">{income.creator_name || '-'}</td>
                                        <td className="text-end fw-bold">{formatCurrency(income.amount)}</td>
                                        <td className="text-center">
                                            <div className="btn-actions">
                                                {hasPermission(PERMISSIONS.EDIT_INCOMES) && (
                                                    <Button variant="secondary" size="sm" onClick={() => handleEditClick(income)} title={t('incomesPage.actions.edit')} aria-label={t('incomesPage.actions.edit')}>
                                                        <FaEdit />
                                                    </Button>
                                                )}
                                                {hasPermission(PERMISSIONS.DELETE_INCOMES) && (
                                                    <Button variant="danger" size="sm" onClick={() => handleDelete(income.income_id)} title={t('incomesPage.actions.delete')} aria-label={t('incomesPage.actions.delete')}>
                                                        <FaTrash />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="7" className="text-center py-4 text-muted">{t('incomesPage.noIncomes')}</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    ) : (
                        /* ── Cards view ── */
                        <div>
                            {incomes.length === 0 ? (
                                <div className="text-center py-5 text-muted">{t('incomesPage.noIncomes')}</div>
                            ) : (
                                <div className="sales-cards-grid">
                                    {incomes.map(income => (
                                        <div key={income.income_id} className="sale-card">
                                            <div className="sale-card__header">
                                                <div className="sale-card__invoice">
                                                    <span className="sale-card__invoice-label">{new Date(income.date).toLocaleDateString('es-CO', { timeZone: 'UTC' })}</span>
                                                </div>
                                                <span className="badge bg-light text-dark border sale-card__badge">
                                                    {t(`incomesPage.categories.${income.category}`, income.category)}
                                                </span>
                                            </div>

                                            <div className="sale-card__client">
                                                <span className="sale-card__client-name">{income.description || '—'}</span>
                                            </div>

                                            <div className="sale-card__amounts">
                                                <div className="sale-card__amount-item">
                                                    <span className="sale-card__amount-label">{t('incomesPage.table.amount')}</span>
                                                    <span className="sale-card__amount-value sale-card__amount-value--paid">{formatCurrency(income.amount)}</span>
                                                </div>
                                                <div className="sale-card__amount-item">
                                                    <span className="sale-card__amount-label">{t('incomesPage.table.paymentMethod')}</span>
                                                    <span className="sale-card__amount-value">{t(`incomesPage.paymentMethods.${income.payment_method}`, income.payment_method || '-')}</span>
                                                </div>
                                            </div>

                                            <div className="sale-card__dates">
                                                <div className="sale-card__date-item">
                                                    <span className="sale-card__date-label">{t('incomesPage.table.registeredBy')}</span>
                                                    <span className="sale-card__date-value">{income.creator_name || '-'}</span>
                                                </div>
                                            </div>

                                            <div className="sale-card__actions btn-actions">
                                                {hasPermission(PERMISSIONS.EDIT_INCOMES) && (
                                                    <Button variant="secondary" size="sm" onClick={() => handleEditClick(income)} title={t('incomesPage.actions.edit')} aria-label={t('incomesPage.actions.edit')}>
                                                        <FaEdit />
                                                    </Button>
                                                )}
                                                {hasPermission(PERMISSIONS.DELETE_INCOMES) && (
                                                    <Button variant="danger" size="sm" onClick={() => handleDelete(income.income_id)} title={t('incomesPage.actions.delete')} aria-label={t('incomesPage.actions.delete')}>
                                                        <FaTrash />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                        <PageSizeSelector pageSize={pageSize} onPageSizeChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setCurrentPage(1); }} />
                    </div>
                </>
            )}

            <CreateIncomeModal
                show={showModal}
                onHide={handleModalClose}
                incomeToEdit={editingIncome}
                onIncomeCreated={fetchIncomes}
                onIncomeUpdated={fetchIncomes}
            />
        </Container>
    );
};

export default IncomesPage;
